import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  Save,
  Download,
  ArrowLeft,
  Eye,
  ExternalLink,
  Send,
  FileText,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import {
  orderApi,
  propertyApi,
  documentOutputFormatApi,
  documentFormApi,
  documentOutputApi,
  orderDocumentApi,
  associationApi,
  managementCompanyApi,
  aiApi,
} from "@/services/api";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { DocumentHeader } from "@/components/orders/DocumentHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  DocumentOutputSection,
  DocumentOutputSubsection,
  DocumentOutputField,
} from "@/models/DocumentOutputFormat";
import { OrderDocument } from "@/models";
import type { FormField as FormFieldDefinition } from "@/models/Form";
import { seedValueWithSource, isYesValue, parseSelectOptions } from "@/utils/processSeeds";
import { formatReleaseDate } from "@/utils/releaseDates";
import { generateTemplatedPdf } from "@/utils/templatePdfUtils";
import { normalizeOrderDocumentFields } from "@/utils/orderDocumentFields";
import {
  ProcessingStatusIndicator,
  ProcessingState,
} from "@/components/ui/processing-status-indicator";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { SynchronizedPdfViewer } from "@/components/ui/synchronized-pdf-viewer";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { downloadStorageFile } from "@/utils/storageRefs";

interface ProcessedSubsection extends DocumentOutputSubsection {
  fields: ProcessedField[];
}

interface ProcessedField extends DocumentOutputField {
  value: string;
  comment: string;
  /** Where the starting value came from; "" when typed or still blank. */
  seedSource?: string;
}

interface ProcessedSection extends DocumentOutputSection {
  subsections: ProcessedSubsection[];
}

/** Loose value equality for review purposes: whitespace and case don't count. */
const normValue = (v: unknown) => String(v ?? "").trim().toLowerCase();

const OrderDocumentOutput = () => {
  const { orderId, documentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [processedData, setProcessedData] = useState<{
    sections: ProcessedSection[];
  } | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessingAndReleasing, setIsProcessingAndReleasing] =
    useState(false);
  const [remainingBalance, setRemainingBalance] = useState<string>("");

  const {
    data: orderData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["backoffice-order-document-output", orderId, documentId],
    queryFn: async () => {
      if (!orderId) throw new Error("Order ID is required");
      const order = await orderApi.getBackofficeOrder(Number(orderId));

      if (!order.requested_documents) {
        throw new Error("No documents found for this order");
      }

      // Find the requested document by its ID
      const document = order.requested_documents.find(
        (doc) => doc.id === Number(documentId)
      );
      if (!document) {
        throw new Error("Document not found");
      }

      let propertyAddress = "No property address found";
      if (order.property_id) {
        try {
          const propertyData = await propertyApi.getById(order.property_id);
          propertyAddress = `${propertyData.address}${
            propertyData.suite_unit ? ` ${propertyData.suite_unit}` : ""
          }, ${propertyData.city}, ${propertyData.state} ${
            propertyData.zip_code
          }`;
        } catch (error) {
          console.error("Error fetching property:", error);
        }
      }

      return {
        document: {
          ...document,
          order_id: +orderId,
          // Normalize fields shape (see src/utils/orderDocumentFields.ts) so
          // rows written after 2026-03-24 (nested section-block shape) render
          // the same as older rows (flat shape).
          fields: normalizeOrderDocumentFields(document.fields as any[]),
        },
        orderId,
        orderDate: order.created_at.toString(),
        association: order.association,
        propertyAddress,
        ipAddress: order.ip_address,
      };
    },
  });

  // The customer's uploaded form is a private file: preview it through a
  // signed link (useSignedUrl), never the stored value itself.
  const { url: uploadedFileUrl } = useSignedUrl(
    orderData?.document?.uploaded_file_path ?? null
  );
  // Fetch document output format sections
  const { data: outputSections, isLoading: loadingOutputSections } = useQuery({
    queryKey: ["documentOutputSections", orderData?.document?.document_id],
    queryFn: () =>
      documentOutputFormatApi.getSections(orderData!.document.document_id!),
    enabled: !!orderData?.document?.document_id,
  });

  // Fetch form data to get field labels
  const { data: formData, isLoading: loadingFormData } = useQuery({
    queryKey: ["document-forms", orderData?.document?.document_id],
    queryFn: () =>
      documentFormApi.list({
        queryKey: [
          "document-forms",
          { document_id: orderData!.document.document_id },
        ],
      }),
    enabled: !!orderData?.document?.document_id,
  });

  // Fetch existing document output
  const { data: documentOutput, isLoading: loadingDocumentOutput } = useQuery({
    queryKey: ["document-output", documentId],
    queryFn: () => documentOutputApi.getByOrderDocumentId(Number(documentId)),
    enabled: !!documentId,
  });

  // The person doing the processing, from the users table: their name, title
  // and phone fill the preparer block of a blank questionnaire.
  const { data: operatorProfile } = useQuery({
    queryKey: ["operator-profile", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("users")
        .select("first_name, last_name, email, phone_number, job_title")
        .eq("auth_user_id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
  });

  // The association's Project Details: its standing answers to the
  // questionnaire, which every blank line starts from.
  const associationId = orderData?.document?.association_id;
  const { data: projectDetails, isLoading: loadingProjectDetails } = useQuery({
    queryKey: ["association-project-details", associationId],
    queryFn: () => associationApi.getProjectDetails(Number(associationId)),
    enabled: !!associationId,
  });

  // Scheduled release: the promised date, and whether this document is
  // already waiting for it ("si entregás antes, dejan de pagar Rush").
  const { data: releasePlan, refetch: refetchReleasePlan } = useQuery({
    queryKey: ["release-plan", orderId, documentId],
    queryFn: async () => {
      const [ordRes, odRes] = await Promise.all([
        supabase.from("orders").select("expected_delivery_date").eq("id", Number(orderId)).maybeSingle(),
        supabase.from("order_documents").select("scheduled_release_at, released").eq("id", Number(documentId)).maybeSingle(),
      ]);
      return {
        expected: (ordRes.data as any)?.expected_delivery_date ?? null,
        scheduledAt: odRes.data?.released ? null : odRes.data?.scheduled_release_at ?? null,
      };
    },
    enabled: !!orderId && !!documentId,
  });
  const expectedDate: string | null = releasePlan?.expected ?? null;
  const scheduledAt: string | null = releasePlan?.scheduledAt ?? null;
  const releaseDateIsFuture =
    !!expectedDate && expectedDate > new Date().toISOString().slice(0, 10);

  // The AI assistant for Process (4-Sep meeting): a questionnaire is FILLED,
  // not compliance-reviewed, so here the AI (1) reviews what the buyer typed
  // and (2) suggests values grounded in Project Details + the trained
  // knowledge base. Gated by the management company's AI switch.
  const aiMcId = (orderData as any)?.association?.management_company_id;
  const { data: aiMc } = useQuery({
    queryKey: ["mc-ai-settings", aiMcId],
    queryFn: () => managementCompanyApi.getById(Number(aiMcId)),
    enabled: !!aiMcId,
  });
  const aiEnabled = !!(aiMc as any)?.ai_settings?.order_analysis_enabled;

  // The review is STORED per order document (7-Sep meeting): the first open
  // computes and saves it; afterwards this returns the saved run with each
  // item's resolution (accepted / rejected / dismissed), so nothing a human
  // already settled gets re-flagged.
  const {
    data: aiHelp,
    isFetching: aiHelpLoading,
  } = useQuery({
    queryKey: ["ai-process-suggestions", documentId],
    queryFn: () => aiApi.getProcessSuggestions(Number(orderId), Number(documentId)),
    enabled: aiEnabled && !!processedData,
    staleTime: Infinity,
    retry: false,
  });

  // Re-running is the ✨ button: greyed until something changes on the form
  // ("si la data cambia, se enciende"). A guided pass walks the unresolved
  // items one by one, like resolving a merge.
  const [aiDirty, setAiDirty] = useState(false);
  const [activeReview, setActiveReview] = useState<
    { kind: "suggestion" | "review" | "empty"; name: string } | null
  >(null);

  // Blank lines the operator looked at and chose to leave blank. A line that
  // says "if applicable" is answered by being empty, so Next settles it
  // instead of leaving it in the count for ever. Unlike the AI's items there
  // is nothing to store it against — it is a decision about this pass, and it
  // lasts as long as the screen is open.
  const [skippedEmpties, setSkippedEmpties] = useState<Set<string>>(new Set());
  const skipEmptyLine = (name: string) =>
    setSkippedEmpties((prev) => new Set(prev).add(name));
  useEffect(() => {
    if (aiHelp) setAiDirty(false);
  }, [aiHelp]);

  // Esc closes the floating review dock without resolving anything.
  useEffect(() => {
    if (!activeReview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveReview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeReview]);

  const aiSuggestionsByName = useMemo(() => {
    const map = new Map<
      string,
      { value: string; reason: string; resolution: string | null }
    >();
    for (const sug of (aiHelp as any)?.suggestions ?? []) {
      if (sug?.name)
        map.set(sug.name, {
          value: String(sug.value),
          reason: String(sug.reason ?? ""),
          resolution: sug.resolution ?? null,
        });
    }
    return map;
  }, [aiHelp]);

  const aiReviewByName = useMemo(() => {
    const map = new Map<
      string,
      { severity: string; message: string; resolution: string | null }
    >();
    for (const item of (aiHelp as any)?.input_review ?? []) {
      if (item?.field_name && !map.has(item.field_name))
        map.set(item.field_name, {
          severity: item.severity === "warning" ? "warning" : "info",
          message: String(item.message),
          resolution: item.resolution ?? null,
        });
    }
    return map;
  }, [aiHelp]);

  // Document order of the lines, so the guided pass moves top to bottom.
  const fieldNameOrder = useMemo(() => {
    const order: string[] = [];
    for (const section of processedData?.sections ?? [])
      for (const sub of section.subsections ?? [])
        for (const f of sub.fields ?? []) if (f.name) order.push(f.name);
    return order;
  }, [processedData]);

  // Where each named line lives, so the review pass can read the current
  // value and the floating dock can apply one.
  const fieldRefByName = useMemo(() => {
    const map = new Map<
      string,
      {
        section: ProcessedSection;
        subsection: ProcessedSubsection;
        field: ProcessedField;
      }
    >();
    for (const section of processedData?.sections ?? [])
      for (const subsection of section.subsections ?? [])
        for (const field of subsection.fields ?? [])
          if (field.name && !map.has(field.name))
            map.set(field.name, { section, subsection, field });
    return map;
  }, [processedData]);

  // Lines with no answer yet. They join the review pass (8-Sep: one box,
  // "que la AI considere las que no tienen — si no se olvida de las obvias").
  // One leaves the list either by being filled in or by being settled with
  // Next — see skippedEmpties.
  const emptyFieldNames = useMemo(() => {
    const names: string[] = [];
    for (const section of processedData?.sections ?? [])
      for (const subsection of section.subsections ?? [])
        for (const field of subsection.fields ?? [])
          if (field.name && String(field.value ?? "").trim() === "")
            names.push(field.name);
    return names;
  }, [processedData]);

  // Everything the pass covers, in document order: the AI's items (minus
  // suggestions that propose what the line already says — noise, 8-Sep bug)
  // plus the blank lines not already covered by an AI item. The dock's
  // "3 of 7" counts against this list.
  const fullReviewList = useMemo(() => {
    const items: Array<{ kind: "suggestion" | "review" | "empty"; name: string }> =
      [];
    const covered = new Set<string>();
    for (const [name] of aiReviewByName) {
      items.push({ kind: "review", name });
      covered.add(name);
    }
    for (const [name, sug] of aiSuggestionsByName) {
      const current = fieldRefByName.get(name)?.field.value;
      if (normValue(current) === normValue(sug.value)) continue;
      items.push({ kind: "suggestion", name });
      covered.add(name);
    }
    for (const name of emptyFieldNames)
      if (!covered.has(name)) items.push({ kind: "empty", name });
    const pos = new Map(fieldNameOrder.map((n, i) => [n, i]));
    items.sort((a, b) => (pos.get(a.name) ?? 9999) - (pos.get(b.name) ?? 9999));
    return items;
  }, [
    aiReviewByName,
    aiSuggestionsByName,
    emptyFieldNames,
    fieldRefByName,
    fieldNameOrder,
  ]);

  const reviewQueue = useMemo(
    () =>
      fullReviewList.filter((it) =>
        it.kind === "review"
          ? !aiReviewByName.get(it.name)?.resolution
          : it.kind === "suggestion"
            ? !aiSuggestionsByName.get(it.name)?.resolution
            : !skippedEmpties.has(it.name)
      ),
    [fullReviewList, aiReviewByName, aiSuggestionsByName, skippedEmpties]
  );

  const aiResolvedCount = useMemo(() => {
    let n = 0;
    for (const [, item] of aiReviewByName) if (item.resolution) n++;
    for (const [, sug] of aiSuggestionsByName) if (sug.resolution) n++;
    // A blank line left blank on purpose is settled too, so the arithmetic in
    // the box ("2 to review - 4 settled") adds up to what the dock walked.
    for (const name of skippedEmpties)
      if (fullReviewList.some((it) => it.kind === "empty" && it.name === name)) n++;
    return n;
  }, [aiReviewByName, aiSuggestionsByName, skippedEmpties, fullReviewList]);

  const rerunAiMutation = useMutation({
    mutationFn: () =>
      aiApi.getProcessSuggestions(Number(orderId), Number(documentId), {
        force: true,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["ai-process-suggestions", documentId], data);
      setAiDirty(false);
      setActiveReview(null);
    },
  });

  const resolveAiMutation = useMutation({
    mutationFn: (vars: {
      kind: "suggestion" | "review";
      name: string;
      resolution: "accepted" | "rejected" | "dismissed" | null;
    }) =>
      aiApi.resolveProcessSuggestion(
        Number(documentId),
        vars.kind,
        vars.name,
        vars.resolution
      ),
    // Optimistic: the chip freezes the moment the operator decides.
    onMutate: (vars) => {
      queryClient.setQueryData(
        ["ai-process-suggestions", documentId],
        (old: any) => {
          if (!old) return old;
          const key = vars.kind === "suggestion" ? "suggestions" : "input_review";
          const nameKey = vars.kind === "suggestion" ? "name" : "field_name";
          return {
            ...old,
            [key]: (old[key] ?? []).map((it: any) =>
              String(it?.[nameKey]) === vars.name
                ? { ...it, resolution: vars.resolution }
                : it
            ),
          };
        }
      );
    },
    onSuccess: (data) =>
      queryClient.setQueryData(["ai-process-suggestions", documentId], data),
  });

  const scrollToAnchor = (name: string) => {
    const el = document.querySelector(`[data-ai-anchor="${name}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  /**
   * Back to the top of the form. The backoffice layout scrolls <main>, not
   * the window, so scrolling the window here would do nothing.
   */
  const scrollPageToTop = () => {
    const main = document.querySelector("main");
    if (main) main.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startAiReview = () => {
    const first = reviewQueue[0];
    if (!first) return;
    setActiveReview(first);
    scrollToAnchor(first.name);
  };

  const goToActiveReview = () => {
    if (activeReview) scrollToAnchor(activeReview.name);
  };

  /** Move to the item after `current` in the queue (or finish the pass). */
  const advanceAiReview = (current: { kind: string; name: string }) => {
    const idx = reviewQueue.findIndex(
      (it) => it.kind === current.kind && it.name === current.name
    );
    let next: { kind: "suggestion" | "review" | "empty"; name: string } | null;
    if (idx >= 0) {
      next = reviewQueue[idx + 1] ?? null;
    } else {
      // The current item left the queue (e.g. a blank line got filled while
      // the dock sat on it) — continue from its document position.
      const pos = new Map(fieldNameOrder.map((n, i) => [n, i]));
      const cur = pos.get(current.name) ?? -1;
      next =
        reviewQueue.find((it) => (pos.get(it.name) ?? 9999) > cur) ?? null;
    }
    setActiveReview(next);
    if (next) {
      setTimeout(() => scrollToAnchor(next.name), 60);
    } else {
      // The pass is over. Come back up so you land on the review box and the
      // Save / Preview / Release row, not on the last line deep in the form.
      scrollPageToTop();
      toast({
        title: "Review complete",
        description:
          "Everything is settled. Release whenever you're ready — nothing sends on its own.",
      });
    }
  };

  const resolveAndAdvance = (
    kind: "suggestion" | "review",
    name: string,
    resolution: "accepted" | "rejected" | "dismissed"
  ) => {
    resolveAiMutation.mutate({ kind, name, resolution });
    if (activeReview) advanceAiReview({ kind, name });
  };

  // The form fields behind the output lines, by id: what type each line is,
  // its options, and who answers it.
  const formFieldsById = useMemo(() => {
    const map = new Map<number, FormFieldDefinition>();
    const forms: any[] = Array.isArray(formData)
      ? formData
      : (formData as any)?.data ?? [];
    for (const form of forms) {
      for (const step of form?.form_steps ?? []) {
        for (const section of step?.form_sections ?? []) {
          for (const field of section?.form_fields ?? []) {
            if (field?.id) map.set(field.id, field);
          }
        }
      }
    }
    return map;
  }, [formData]);

  // Mutations for document output operations
  const createDocumentOutputMutation = useMutation({
    mutationFn: documentOutputApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["document-output", documentId],
      });
      toast({
        title: "Processing Started",
        description: "Document output processing has been initialized.",
      });
    },
    onError: (error) => {
      console.error("Error creating document output:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to start processing. Please try again.",
      });
    },
  });

  const updateDocumentOutputMutation = useMutation({
    mutationFn: ({
      orderDocumentId,
      data,
    }: {
      orderDocumentId: number;
      data: any;
    }) => documentOutputApi.update(orderDocumentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["document-output", documentId],
      });
      setHasChanges(false);
      toast({
        title: "Changes Saved",
        description: "Your changes have been saved successfully.",
      });
    },
    onError: (error) => {
      console.error("Error updating document output:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save changes. Please try again.",
      });
    },
  });

  useEffect(() => {
    if (outputSections && orderData?.document && !loadingProjectDetails) {
      processDocumentData();
    }
  }, [outputSections, orderData?.document, documentOutput, projectDetails, loadingProjectDetails, operatorProfile]);

  const processDocumentData = () => {
    if (!outputSections || !orderData?.document) return;

    try {
      // If we have existing document output data, use it; otherwise, process from scratch
      const existingData = documentOutput?.output_data;

      // What the order already carries, by field name: the platform's own
      // answers (association, management company) that other lines copy.
      const orderAnswers: Record<string, string> = {};
      for (const f of orderData.document.fields ?? []) {
        const name = (f as any).field_name;
        if (name && f.value && String(f.value).trim() !== "") {
          orderAnswers[name] = String(f.value);
        }
      }

      const processedSections = outputSections.map((section) => {
        const processedSubsections = (section.subsections || []).map(
          (subsection) => {
            const processedFields = (subsection.fields || []).map((field) => {
              let finalValue = field.value || "";
              let finalComment = field.comment || "";

              // First, check if we have existing saved data for this field
              if (existingData && existingData.sections) {
                const existingSection = existingData.sections.find(
                  (s: any) => s.id === section.id
                );
                if (existingSection && existingSection.subsections) {
                  const existingSubsection = existingSection.subsections.find(
                    (ss: any) => ss.id === subsection.id
                  );
                  if (existingSubsection && existingSubsection.fields) {
                    const existingField = existingSubsection.fields.find(
                      (f: any) => f.id === field.id
                    );
                    if (existingField) {
                      finalValue = existingField.value || "";
                      finalComment = existingField.comment || "";
                    }
                  }
                }
              }

              // If field value is still empty and it references a form field, populate from form submission
              let seedSource = "";
              if (
                (!finalValue || finalValue.trim() === "") &&
                field.value_source === "user_input" &&
                field.referenced_field_id
              ) {
                const submittedField = orderData.document.fields?.find(
                  (f) => f.field_id === field.referenced_field_id
                );
                if (submittedField) {
                  // Get all values for this field (in case it's a multi-value field)
                  const allValues =
                    orderData.document.fields
                      ?.filter((f) => f.field_id === field.referenced_field_id)
                      ?.map((f) => f.value)
                      ?.filter((v) => v && v.trim() !== "") || [];

                  if (allValues.length > 0) {
                    finalValue = allValues.join(", ");
                    seedSource = "order";
                  }
                }
              }

              // Still blank: the association's Project Details, then what the
              // spec says to copy from the order, then the person processing.
              if (!finalValue || finalValue.trim() === "") {
                const seeded = seedValueWithSource(field.name, {
                  projectDetails: projectDetails?.answers,
                  orderAnswers,
                  user: {
                    firstName: user?.firstName ?? operatorProfile?.first_name,
                    lastName: user?.lastName ?? operatorProfile?.last_name,
                    email: user?.email ?? operatorProfile?.email,
                    phone_number: operatorProfile?.phone_number,
                    job_title: operatorProfile?.job_title,
                  },
                });
                if (seeded.value) {
                  finalValue = seeded.value;
                  seedSource = seeded.source;
                }
              }

              // The preparer is whoever is processing NOW. A saved snapshot
              // carries the FIRST operator's name (or a blank, if their
              // profile had not loaded), so Mauro opened a document and saw
              // someone else on the signature line. These five lines always
              // follow the signed-in account; they stay editable like the rest.
              if (
                field.name &&
                ["prep_name", "prep_title", "prep_phone", "prep_email", "prep_date"].includes(field.name)
              ) {
                const own = seedValueWithSource(field.name, {
                  user: {
                    firstName: user?.firstName ?? operatorProfile?.first_name,
                    lastName: user?.lastName ?? operatorProfile?.last_name,
                    email: user?.email ?? operatorProfile?.email,
                    phone_number: operatorProfile?.phone_number,
                    job_title: operatorProfile?.job_title,
                  },
                });
                if (own.value) {
                  finalValue = own.value;
                  seedSource = own.source;
                }
              }

              return {
                ...field,
                value: finalValue,
                comment: finalComment,
                seedSource,
              } as ProcessedField;
            });

            return {
              ...subsection,
              fields: processedFields,
            } as ProcessedSubsection;
          }
        );

        return {
          ...section,
          subsections: processedSubsections,
        } as ProcessedSection;
      })
        // A section with no subsections (a letterhead-less "Layout") has
        // nothing to process — its lone header would just sit there.
        .filter((section) => section.subsections.length > 0);

      setProcessedData({
        sections: processedSections,
      });

      // If no document output exists and we have processed data, create it
      if (!documentOutput && !createDocumentOutputMutation.isPending) {
        const dataToSave = {
          sections: processedSections,
        };

        createDocumentOutputMutation.mutate({
          order_document_id: Number(documentId),
          output_data: dataToSave,
        });
      }
    } catch (error) {
      console.error("Error processing document data:", error);
      toast({
        variant: "destructive",
        title: "Error processing document",
        description: "Failed to process document output format data.",
      });
    }
  };

  const handleFieldValueChange = (
    sectionId: number,
    subsectionId: number,
    fieldId: number,
    newValue: string
  ) => {
    if (!processedData) return;

    const updatedSections = processedData.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId) {
            const updatedFields = subsection.fields.map((field) => {
              if (field.id === fieldId) {
                return { ...field, value: newValue };
              }
              return field;
            });
            return { ...subsection, fields: updatedFields };
          }
          return subsection;
        });
        return { ...section, subsections: updatedSubsections };
      }
      return section;
    });

    setProcessedData({ sections: updatedSections });
    setHasChanges(true);
    // A value changed since the stored AI run — the ✨ re-run button lights up.
    setAiDirty(true);
  };

  const handleFieldCommentChange = (
    sectionId: number,
    subsectionId: number,
    fieldId: number,
    newComment: string
  ) => {
    if (!processedData) return;

    const updatedSections = processedData.sections.map((section) => {
      if (section.id === sectionId) {
        const updatedSubsections = section.subsections.map((subsection) => {
          if (subsection.id === subsectionId) {
            const updatedFields = subsection.fields.map((field) => {
              if (field.id === fieldId) {
                return { ...field, comment: newComment };
              }
              return field;
            });
            return { ...subsection, fields: updatedFields };
          }
          return subsection;
        });
        return { ...section, subsections: updatedSubsections };
      }
      return section;
    });

    setProcessedData({ sections: updatedSections });
    setHasChanges(true);
  };

  const handleFinishProcessing = async (mode: "now" | "schedule" = "now") => {
    if (!processedData) return;

    setIsProcessingAndReleasing(true);

    try {
      // First generate the PDF
      let pdfBlob = null;
      if (orderData?.document?.document?.document_type_id) {
        try {
          const { pdfBlob: generatedPdf } = await generateTemplatedPdf({
            orderDocument: {
              ...orderData.document,
              remaining_balance: +remainingBalance,
            },
            processedSections: processedData.sections,
            user,
          });
          pdfBlob = generatedPdf;
        } catch (pdfError) {
          console.error("Error generating PDF for release:", pdfError);
          // Continue without PDF if generation fails
        }
      }

      // Convert PDF to base64 if we have one
      let pdfBase64 = null;
      if (pdfBlob) {
        try {
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const bytes = [...new Uint8Array(arrayBuffer)];
          const binaryString = bytes
            .map((byte) => String.fromCharCode(byte))
            .join("");
          pdfBase64 = btoa(binaryString);
        } catch (conversionError) {
          console.error("Error converting PDF to base64:", conversionError);
        }
      }

      // Update processing state and release
      try {
        // Now release with PDF data
        const releasedDoc: any = await orderDocumentApi.updateReleaseStatus(
          Number(documentId),
          true,
          pdfBase64, // Pass the PDF data
          mode === "schedule" && expectedDate
            ? { scheduledFor: `${expectedDate}T14:00:00Z` }
            : undefined
        );
        const document_id = releasedDoc?.document_id ?? releasedDoc?.id;

        if (documentOutput) {
          await updateDocumentOutputMutation.mutateAsync({
            orderDocumentId: Number(documentId),
            data: {
              output_data: processedData,
              processing_state: "processed",
            },
          });
        } else {
          await createDocumentOutputMutation.mutateAsync({
            order_document_id: Number(documentId),
            output_data: processedData,
            processing_state: "processed",
          } as any);
        }

        if (mode === "schedule") {
          toast({
            title: "Release scheduled",
            description: `To be released on ${formatReleaseDate(expectedDate)} — the promised delivery date. Use Release now to send it earlier.`,
          });
        } else if (document_id) {
          toast({
            title: "Document released",
            description:
              "The requester has been emailed the document. You can change it and release again.",
          });
        }
        refetchReleasePlan();
      } catch (releaseError) {
        console.error("Error releasing document:", releaseError);
        toast({
          variant: "destructive",
          title: "Release failed",
          description:
            "The document could not be released. Save your changes and try again.",
        });
      }

      // Refresh the document output data
      queryClient.invalidateQueries({
        queryKey: ["document-output", documentId],
      });
    } catch (error) {
      console.error("Error processing document:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to process the document. Please try again.",
      });
    } finally {
      setIsProcessingAndReleasing(false);
    }
  };

  // Saving is always available: it keeps whatever state the output is in, and
  // the very first save creates the row.
  const isSavingOutput =
    createDocumentOutputMutation.isPending || updateDocumentOutputMutation.isPending;
  const handleSaveOutput = () => {
    if (!processedData) return;
    if (documentOutput) {
      updateDocumentOutputMutation.mutate({
        orderDocumentId: Number(documentId),
        data: {
          output_data: processedData,
          processing_state: documentOutput.processing_state,
        },
      });
    } else {
      createDocumentOutputMutation.mutate({
        order_document_id: Number(documentId),
        output_data: processedData,
        processing_state: "pending",
      } as any);
    }
  };

  /** A select cannot carry an empty value; this stands in for "not answered". */
  const BLANK = "__blank__";

  const fieldLabelByName = useMemo(() => {
    const map = new Map<string, string>();
    formFieldsById.forEach((f) => {
      if (f?.name) map.set(f.name, f.label ?? f.name);
    });
    return map;
  }, [formFieldsById]);

  // What the floating dock is showing right now (8-Sep: the actions live in a
  // dock at the bottom of the screen, not inside the line itself).
  const activeDockItem = useMemo(() => {
    if (!activeReview) return null;
    const ref = fieldRefByName.get(activeReview.name) ?? null;
    const title =
      ref?.field.title ||
      fieldLabelByName.get(activeReview.name) ||
      activeReview.name;
    if (activeReview.kind === "suggestion") {
      const sug = aiSuggestionsByName.get(activeReview.name);
      return sug ? { kind: "suggestion" as const, title, ref, sug } : null;
    }
    if (activeReview.kind === "review") {
      const rev = aiReviewByName.get(activeReview.name);
      return rev ? { kind: "review" as const, title, ref, rev } : null;
    }
    return { kind: "empty" as const, title, ref };
  }, [
    activeReview,
    fieldRefByName,
    fieldLabelByName,
    aiSuggestionsByName,
    aiReviewByName,
  ]);

  const dockPosition = useMemo(() => {
    if (!activeReview) return { pos: 0, total: 0 };
    const pos =
      fullReviewList.findIndex(
        (it) => it.kind === activeReview.kind && it.name === activeReview.name
      ) + 1;
    return { pos, total: fullReviewList.length };
  }, [activeReview, fullReviewList]);

  const goPrevReview = () => {
    if (!activeReview) return;
    const idx = reviewQueue.findIndex(
      (it) => it.kind === activeReview.kind && it.name === activeReview.name
    );
    const prev = idx > 0 ? reviewQueue[idx - 1] : null;
    if (prev) {
      setActiveReview(prev);
      setTimeout(() => scrollToAnchor(prev.name), 60);
    }
  };

  /**
   * The ✨ chip under a line: the AI's grounded proposal. Accept applies the
   * value and freezes the chip; Ignore freezes it without applying. Frozen
   * chips stay visible ("eso debería quedar como frizado") with an Undo.
   */
  const renderSuggestionChip = (
    section: ProcessedSection,
    subsection: ProcessedSubsection,
    field: ProcessedField
  ) => {
    if (!field.name) return null;
    const sug = aiSuggestionsByName.get(field.name);
    if (!sug) return null;
    const isActive =
      activeReview?.kind === "suggestion" && activeReview.name === field.name;

    if (sug.resolution === "accepted" || sug.resolution === "rejected") {
      return (
        <div className="mt-1 flex items-start gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-500">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span className="min-w-0">
            <span className="font-medium">
              {sug.resolution === "accepted" ? "Applied" : "Ignored"}: {sug.value}
            </span>
            {sug.reason && <span> — {sug.reason}</span>}
          </span>
          <button
            type="button"
            className="ml-auto flex-shrink-0 underline decoration-dotted hover:text-gray-700"
            onClick={() =>
              resolveAiMutation.mutate({
                kind: "suggestion",
                name: field.name!,
                resolution: null,
              })
            }
          >
            Undo
          </button>
        </div>
      );
    }

    // A suggestion proposing what the line already says is noise (8-Sep bug:
    // "sugiere lo mismo que está escrito") — compare loosely and hide it.
    if (normValue(field.value) === normValue(sug.value)) return null;

    // Unresolved: a compact marker only — clicking it opens the item in the
    // floating dock, where Apply/Ignore live (8-Sep: "no todo en el input").
    return (
      <button
        type="button"
        onClick={() =>
          setActiveReview({ kind: "suggestion", name: field.name! })
        }
        className={
          "mt-1 flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-xs " +
          (isActive
            ? "border-violet-400 bg-violet-100 text-violet-900 ring-2 ring-violet-300"
            : "border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100")
        }
      >
        <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="min-w-0 truncate">
          <span className="font-medium">AI suggests: {sug.value}</span>
          {sug.reason && <span className="text-violet-600"> — {sug.reason}</span>}
        </span>
        <span className="ml-auto flex-shrink-0 font-semibold underline decoration-dotted">
          {isActive ? "Reviewing…" : "Review"}
        </span>
      </button>
    );
  };

  /**
   * The AI's warning about what the REQUESTOR typed, shown on the line it
   * refers to ("mostrar el error en su contexto"). OK dismisses it.
   */
  const renderInputReviewChip = (field: ProcessedField) => {
    if (!field.name) return null;
    const item = aiReviewByName.get(field.name);
    if (!item) return null;
    const isActive =
      activeReview?.kind === "review" && activeReview.name === field.name;

    if (item.resolution === "dismissed") {
      return (
        <div className="mt-1 flex items-start gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-500">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span className="min-w-0">Reviewed: {item.message}</span>
          <button
            type="button"
            className="ml-auto flex-shrink-0 underline decoration-dotted hover:text-gray-700"
            onClick={() =>
              resolveAiMutation.mutate({
                kind: "review",
                name: field.name!,
                resolution: null,
              })
            }
          >
            Undo
          </button>
        </div>
      );
    }

    const warning = item.severity === "warning";
    // Unresolved: compact marker; the OK/Skip actions live in the dock.
    return (
      <button
        type="button"
        onClick={() => setActiveReview({ kind: "review", name: field.name! })}
        className={
          "mt-1 flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-xs " +
          (warning
            ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
            : "border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100") +
          (isActive ? " ring-2 ring-violet-300" : "")
        }
      >
        <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="min-w-0 truncate">
          <span className="font-medium">AI flags:</span> {item.message}
        </span>
        <span className="ml-auto flex-shrink-0 font-semibold underline decoration-dotted">
          {isActive ? "Reviewing…" : "Review"}
        </span>
      </button>
    );
  };

  /**
   * The editor for one line, chosen by the form field behind it: a box where
   * the form has a box, a list where it has options, a date where it has a
   * date, read-only where the platform answered from the order.
   */
  const renderValueEditor = (
    section: ProcessedSection,
    subsection: ProcessedSubsection,
    field: ProcessedField
  ) => {
    const definition = field.referenced_field_id
      ? formFieldsById.get(field.referenced_field_id)
      : undefined;
    const setValue = (value: string) =>
      handleFieldValueChange(section.id, subsection.id, field.id, value);

    // Lines the platform answered stay editable like everything else —
    // "permitiría que todo se pueda modificar" (Mauro, 2 Sep) — they just say
    // where their value came from. The provenance doubles as the certainty
    // Mauro asked for on 3 Sep: what was auto-filled, from where, and what
    // still needs a human.
    const sourceLabel =
      definition?.filled_by === "system" || field.seedSource === "order"
        ? "From the order"
        : field.seedSource === "project_details"
          ? "From Project Details"
          : field.seedSource === "operator"
            ? "From your profile"
            : null;
    const caption = sourceLabel ? (
      <div className="text-xs text-muted-foreground">{sourceLabel}</div>
    ) : !field.value || field.value.trim() === "" ? (
      <div className="text-xs text-amber-600">Needs review</div>
    ) : null;

    if (definition?.field_type === "checkbox") {
      const checked = isYesValue(field.value);
      return (
        <label className="flex items-center gap-2 py-1.5 text-sm cursor-pointer">
          <Checkbox
            checked={checked}
            onCheckedChange={(next) => setValue(next ? "Yes" : "No")}
          />
          <span>{field.value ? (checked ? "Yes" : "No") : "Not answered"}</span>
        </label>
      );
    }

    if (definition?.field_type === "select") {
      const options = parseSelectOptions(definition.content);
      return (
        <div className="space-y-1 pr-1">
          <Select
            value={field.value || BLANK}
            onValueChange={(next) => setValue(next === BLANK ? "" : next)}
          >
            <SelectTrigger className="text-sm focus:ring-offset-0">
              <SelectValue placeholder="Not answered" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={BLANK}>
                <span className="text-muted-foreground">Not answered</span>
              </SelectItem>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {caption}
        </div>
      );
    }

    if (definition?.field_type === "date") {
      return (
        <div className="space-y-1 pr-1">
          <Input
            type="date"
            value={field.value || ""}
            onChange={(e) => setValue(e.target.value)}
            className="text-sm focus-visible:ring-offset-0"
          />
          {caption}
        </div>
      );
    }

    return (
      <div className="space-y-1 pr-1">
        {field.value && field.value.length > 100 ? (
          <Textarea
            value={field.value || ""}
            onChange={(e) => setValue(e.target.value)}
            className="min-h-20 text-sm focus-visible:ring-offset-0"
            placeholder="Enter value..."
          />
        ) : (
          <Input
            value={field.value || ""}
            onChange={(e) => setValue(e.target.value)}
            className="text-sm focus-visible:ring-offset-0"
            placeholder="Enter value..."
          />
        )}
        {caption}
      </div>
    );
  };

  const getProcessingState = (): ProcessingState => {
    if (!documentOutput) return "unprocessed";
    return documentOutput.processing_state as ProcessingState;
  };

  const getActionButtonText = () => {
    const state = getProcessingState();
    switch (state) {
      case "unprocessed":
        return "Start Processing";
      case "pending":
        return "Move to Processed";
      case "processed":
        return "Update & Reprocess";
      default:
        return "Process";
    }
  };

  const handleGeneratePdf = async () => {
    if (!processedData || !orderData?.document?.document?.document_type_id) {
      toast({
        variant: "destructive",
        title: "Missing data",
        description:
          "Cannot generate PDF without processed data or document type.",
      });
      return;
    }

    setIsGeneratingPdf(true);
    try {
      // Generate PDF with template processing
      const { pdfBlob, filename } = await generateTemplatedPdf({
        orderDocument: {
          ...orderData.document,
          remaining_balance: +remainingBalance,
        },
        processedSections: processedData.sections,
        user,
      });

      // Download the PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Mark as downloaded
      if (documentOutput) {
        try {
          await documentOutputApi.markAsDownloaded(Number(documentId));
          queryClient.invalidateQueries({
            queryKey: ["document-output", documentId],
          });
        } catch (error) {
          console.error("Error marking as downloaded:", error);
        }
      }

      toast({
        title: "PDF Generated",
        description: "Document PDF has been generated successfully.",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        variant: "destructive",
        title: "PDF Generation Failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to generate PDF. Please try again.",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownloadCustomForm = () => {
    if (!orderData?.document?.uploaded_file_path) return;

    // A private file: download it through a signed link.
    void downloadStorageFile(
      orderData.document.uploaded_file_path,
      `custom-form-${orderData.document.name || "document"}.pdf`
    );

    toast({
      title: "Download Started",
      description: "Custom form download has started.",
    });
  };

  const handlePreviewCustomForm = () => {
    setShowPreview(!showPreview);
  };

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </BackofficeLayout>
    );
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="space-y-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <p className="text-gray-500">Error fetching document</p>
        </div>
      </BackofficeLayout>
    );
  }

  if (!orderData) {
    return (
      <BackofficeLayout>
        <div className="space-y-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <p className="text-gray-500">Document not found</p>
        </div>
      </BackofficeLayout>
    );
  }

  const isDataLoading =
    loadingOutputSections || loadingFormData || loadingDocumentOutput;

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Order
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-orange-600" />
                    Process document
                  </CardTitle>
                  <CardDescription>
                    {orderData?.document.document?.name ||
                      `Document #${orderData?.document.document_id}`}
                  </CardDescription>
                  {/* The "164 of 166 lines pre-filled · … · 2 to review" line
                      is gone on purpose (8-Sep: "esa línea debería volar") —
                      the single review box below carries the count now. */}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Remaining Balance Input */}
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="remaining-balance"
                    className="text-sm font-medium text-gray-700"
                  >
                    Remaining Balance:
                  </label>
                  <Input
                    id="remaining-balance"
                    type="text"
                    value={remainingBalance}
                    onChange={(e) => setRemainingBalance(e.target.value)}
                    placeholder="$0.00"
                    className="w-32 text-sm"
                  />
                </div>

                {/* Guardar, Previsualizar y Liberar — the three of them,
                    always. Save keeps work; Preview shows the PDF without
                    changing anything; Release emails the requester and can be
                    done again after changes (Mauro, 2 Sep). */}
                <Button
                  variant="outline"
                  onClick={handleSaveOutput}
                  disabled={isSavingOutput}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSavingOutput ? "Saving..." : "Save"}
                </Button>

                <Button
                  variant="outline"
                  onClick={handleGeneratePdf}
                  disabled={isGeneratingPdf}
                >
                  {isGeneratingPdf ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </>
                  )}
                </Button>

                {scheduledAt && (
                  <span className="self-center text-xs text-muted-foreground whitespace-nowrap">
                    To be released on{" "}
                    <strong className="font-semibold">
                      {formatReleaseDate(scheduledAt)}
                    </strong>
                  </span>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      disabled={isProcessingAndReleasing}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {isProcessingAndReleasing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Releasing...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          {scheduledAt ? "Release now" : "Release"}
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Release document</AlertDialogTitle>
                      <AlertDialogDescription>
                        {scheduledAt ? (
                          <>
                            This document already has a release scheduled for{" "}
                            <strong className="font-semibold">
                              {formatReleaseDate(scheduledAt)}
                            </strong>{" "}
                            with its PDF stored. Release now emails the document
                            immediately — regenerated as it looks right now —
                            instead of waiting for that date.
                          </>
                        ) : releaseDateIsFuture ? (
                          <>
                            The promised delivery date is{" "}
                            <strong className="font-semibold">
                              {formatReleaseDate(expectedDate)}
                            </strong>
                            . Scheduling holds the email until then — releasing
                            early teaches customers they never need Rush.
                          </>
                        ) : (
                          <>The requester gets the document by email, as it is
                          right now. You can keep editing and release again
                          later.</>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      {releaseDateIsFuture && !scheduledAt && (
                        <AlertDialogAction
                          onClick={() => handleFinishProcessing("now")}
                          disabled={isProcessingAndReleasing}
                        >
                          Release now
                        </AlertDialogAction>
                      )}
                      <AlertDialogAction
                        onClick={() =>
                          handleFinishProcessing(
                            releaseDateIsFuture && !scheduledAt ? "schedule" : "now"
                          )
                        }
                        disabled={isProcessingAndReleasing}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        {releaseDateIsFuture && !scheduledAt
                          ? `Schedule for ${formatReleaseDate(expectedDate)}`
                          : "Release"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Custom Form Message */}
            {orderData?.document?.uploaded_file_path && (
              <Card className="bg-blue-50 border-blue-200 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">
                        The user has provided a custom form
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadCustomForm}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Click to download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviewCustomForm}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {showPreview ? "Hide Preview" : "Preview"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ONE review box, always (8-Sep): without AI it goes amber and
                only walks the blank lines; with AI it goes violet and the
                count folds the blank lines into the AI's items. */}
            {!aiEnabled && reviewQueue.length > 0 && (
              <div className="mb-4 rounded-md border border-amber-300 bg-amber-50/80 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                  <FileText className="h-4 w-4 flex-shrink-0" />
                  We have {reviewQueue.length}{" "}
                  {reviewQueue.length === 1 ? "field" : "fields"} to review
                  <button
                    type="button"
                    className="ml-1 rounded border border-amber-400 px-2 py-0.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    onClick={activeReview ? goToActiveReview : startAiReview}
                  >
                    {activeReview ? "Continue review" : "Review"}
                  </button>
                </div>
              </div>
            )}

            {aiEnabled && (aiHelpLoading || aiHelp || rerunAiMutation.isPending) && (
              <div className="mb-4 rounded-md border border-violet-200 bg-violet-50/70 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-violet-900">
                  <Sparkles className="h-4 w-4 flex-shrink-0" />
                  {aiHelpLoading || rerunAiMutation.isPending ? (
                    <>
                      Reviewing the buyer's input and looking for suggestions…
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    </>
                  ) : (
                    <>
                      AI review
                      {reviewQueue.length > 0 ? (
                        <>
                          <span className="font-normal text-violet-700">
                            · {reviewQueue.length}{" "}
                            {reviewQueue.length === 1 ? "thing" : "things"} to
                            review
                            {aiResolvedCount > 0 && <> · {aiResolvedCount} settled</>}
                          </span>
                          <button
                            type="button"
                            className="ml-1 rounded border border-violet-300 px-2 py-0.5 text-xs font-semibold text-violet-800 hover:bg-violet-100"
                            onClick={activeReview ? goToActiveReview : startAiReview}
                          >
                            {activeReview ? "Continue review" : "Review"}
                          </button>
                        </>
                      ) : (
                        <span className="font-normal text-violet-700">
                          {aiResolvedCount > 0 ? (
                            <>· all {aiResolvedCount} settled ✓</>
                          ) : (
                            <>
                              · nothing to review — the buyer's input looks
                              fine and every line has an answer.
                            </>
                          )}
                        </span>
                      )}
                      {/* Re-run: greyed until something on the form changes. */}
                      <button
                        type="button"
                        title={
                          aiDirty
                            ? "Something changed — run the AI review again"
                            : "Nothing changed since the last run"
                        }
                        aria-label="Re-run the AI review"
                        disabled={!aiDirty}
                        className={
                          "ml-auto rounded p-1 transition-colors " +
                          (aiDirty
                            ? "text-violet-700 hover:bg-violet-100"
                            : "cursor-default text-violet-300")
                        }
                        onClick={() => aiDirty && rerunAiMutation.mutate()}
                      >
                        <Sparkles className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* The floating review dock (8-Sep): the guided pass happens HERE,
                at the bottom of the screen — the line itself only scrolls into
                view and highlights; it carries no action buttons. */}
            {activeReview && activeDockItem && (
              <div className="pointer-events-none fixed inset-x-0 bottom-5 z-50 flex justify-center px-4">
                <div
                  className={
                    "pointer-events-auto w-full max-w-xl rounded-lg border bg-white shadow-2xl " +
                    (aiEnabled ? "border-violet-300" : "border-amber-300")
                  }
                >
                  <div
                    className={
                      "flex items-center gap-2 rounded-t-lg border-b px-3 py-1.5 " +
                      (aiEnabled
                        ? "border-violet-100 bg-violet-50/80"
                        : "border-amber-100 bg-amber-50/80")
                    }
                  >
                    {aiEnabled ? (
                      <Sparkles className="h-4 w-4 flex-shrink-0 text-violet-700" />
                    ) : (
                      <FileText className="h-4 w-4 flex-shrink-0 text-amber-700" />
                    )}
                    <span
                      className={
                        "text-xs font-semibold " +
                        (aiEnabled ? "text-violet-900" : "text-amber-900")
                      }
                    >
                      {aiEnabled ? "AI review" : "Review"}
                    </span>
                    <span
                      className={
                        "text-xs " +
                        (aiEnabled ? "text-violet-600" : "text-amber-700")
                      }
                    >
                      {dockPosition.pos > 0
                        ? `· ${dockPosition.pos} of ${dockPosition.total}`
                        : `· ${reviewQueue.length} to go`}
                    </span>
                    <div className="ml-auto flex items-center gap-0.5">
                      <button
                        type="button"
                        title="Previous"
                        disabled={
                          reviewQueue.findIndex(
                            (it) =>
                              it.kind === activeReview.kind &&
                              it.name === activeReview.name
                          ) <= 0
                        }
                        className="rounded p-1 text-gray-600 hover:bg-gray-100 disabled:cursor-default disabled:text-gray-300"
                        onClick={goPrevReview}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Skip to the next one"
                        className="rounded p-1 text-gray-600 hover:bg-gray-100"
                        onClick={() => advanceAiReview(activeReview)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Close (Esc)"
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        onClick={() => setActiveReview(null)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-gray-900 underline-offset-2 hover:underline"
                      title="Scroll to this line"
                      onClick={goToActiveReview}
                    >
                      {activeDockItem.title}
                    </button>
                    {activeDockItem.kind === "suggestion" ? (
                      <div className="mt-0.5 space-y-0.5">
                        <div className="text-sm text-violet-900">
                          AI suggests:{" "}
                          <span className="font-semibold">
                            {activeDockItem.sug.value}
                          </span>
                          {activeDockItem.sug.reason && (
                            <span className="text-violet-600">
                              {" "}
                              — {activeDockItem.sug.reason}
                            </span>
                          )}
                        </div>
                        {String(activeDockItem.ref?.field.value ?? "").trim() !==
                          "" && (
                          <div className="text-xs text-gray-500">
                            Current: {activeDockItem.ref?.field.value}
                          </div>
                        )}
                      </div>
                    ) : activeDockItem.kind === "review" ? (
                      <div
                        className={
                          "mt-0.5 text-sm " +
                          (activeDockItem.rev.severity === "warning"
                            ? "text-amber-800"
                            : "text-violet-800")
                        }
                      >
                        {activeDockItem.rev.message}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-sm text-gray-600">
                        This line has no answer yet. Fill it in on the form, or
                        leave it blank if it does not apply — Next settles it
                        either way.
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 px-3 pb-2.5">
                    {activeDockItem.kind === "suggestion" ? (
                      <>
                        <Button
                          size="sm"
                          className="h-7 bg-violet-600 px-3 text-xs hover:bg-violet-700"
                          disabled={!activeDockItem.ref}
                          onClick={() => {
                            const ref = activeDockItem.ref;
                            if (ref)
                              handleFieldValueChange(
                                ref.section.id,
                                ref.subsection.id,
                                ref.field.id,
                                activeDockItem.sug.value
                              );
                            resolveAndAdvance(
                              "suggestion",
                              activeReview.name,
                              "accepted"
                            );
                          }}
                        >
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs"
                          onClick={() =>
                            resolveAndAdvance(
                              "suggestion",
                              activeReview.name,
                              "rejected"
                            )
                          }
                        >
                          Ignore
                        </Button>
                      </>
                    ) : activeDockItem.kind === "review" ? (
                      <Button
                        size="sm"
                        className="h-7 bg-violet-600 px-3 text-xs hover:bg-violet-700"
                        onClick={() =>
                          resolveAndAdvance(
                            "review",
                            activeReview.name,
                            "dismissed"
                          )
                        }
                      >
                        OK, reviewed
                      </Button>
                    ) : (
                      /* A blank line is settled by being looked at: fill it in
                         and it leaves the list on its own, or say Next and it
                         stops counting. "If applicable" means blank is an
                         answer. */
                      <Button
                        size="sm"
                        className={
                          "h-7 px-3 text-xs " +
                          (aiEnabled
                            ? "bg-violet-600 hover:bg-violet-700"
                            : "bg-amber-600 hover:bg-amber-700")
                        }
                        onClick={() => {
                          skipEmptyLine(activeReview.name);
                          advanceAiReview(activeReview);
                        }}
                      >
                        Next
                      </Button>
                    )}
                    {activeDockItem.kind !== "empty" && (
                      <button
                        type="button"
                        className="ml-auto text-xs text-gray-500 underline decoration-dotted hover:text-gray-700"
                        onClick={() => advanceAiReview(activeReview)}
                      >
                        Skip for now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isDataLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Loading document output format...</span>
              </div>
            ) : !processedData?.sections ||
              processedData.sections.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No document output format configured for this document.
                </p>
              </div>
            ) : (
              /* Extra bottom room while the dock floats over the page, so the
                 last lines can still scroll clear of it. */
              <div className={"space-y-6" + (activeReview ? " pb-44" : "")}>
                {/* Split Layout when Preview is Active */}
                {showPreview && orderData?.document?.uploaded_file_path && uploadedFileUrl ? (
                  <SynchronizedPdfViewer
                    pdfUrl={uploadedFileUrl}
                  >
                    <table className="w-full table-fixed">
                      <colgroup>
                        <col className="w-1/4" />
                        <col className="w-5/12" />
                        <col className="w-1/3" />
                      </colgroup>
                      <tbody>
                        {processedData.sections.map((section) => (
                          <React.Fragment key={section.id}>
                            {/* Section Header Row */}
                            <tr>
                              <td
                                colSpan={3}
                                className="p-3 bg-blue-100 font-bold text-left border-b text-blue-900"
                              >
                                {section.title}
                              </td>
                            </tr>

                            {/* Section Subsections */}
                            {section.subsections &&
                            section.subsections.length > 0 ? (
                              section.subsections.map((subsection) => (
                                <React.Fragment key={subsection.id}>
                                  {/* Subsection Header Row */}
                                  <tr>
                                    <td
                                      colSpan={3}
                                      className="p-3 bg-gray-100 font-semibold text-left border-b text-gray-800 pl-6"
                                    >
                                      {subsection.title}
                                    </td>
                                  </tr>

                                  {/* Subsection Fields */}
                                  {subsection.fields &&
                                  subsection.fields.length > 0 ? (
                                    subsection.fields.map((field) => (
                                      <tr
                                        key={field.id}
                                        data-ai-anchor={field.name || undefined}
                                        className={
                                          "border-b transition-colors " +
                                          (activeReview &&
                                          field.name === activeReview.name
                                            ? "bg-violet-100 outline outline-2 -outline-offset-2 outline-violet-400"
                                            : "bg-white")
                                        }
                                      >
                                        <td className="p-3 font-medium text-left border-r align-top pl-12">
                                          {field.title}
                                        </td>
                                        <td className="p-3 text-left border-r align-top">
                                          <div className="space-y-1">
                                            {renderValueEditor(section, subsection, field)}
                                            {renderInputReviewChip(field)}
                                            {renderSuggestionChip(section, subsection, field)}
                                          </div>
                                        </td>
                                        <td className="p-3 text-left align-top">
                                          <div className="space-y-1">
                                            <Textarea
                                              value={field.comment || ""}
                                              onChange={(e) =>
                                                handleFieldCommentChange(
                                                  section.id,
                                                  subsection.id,
                                                  field.id,
                                                  e.target.value
                                                )
                                              }
                                              className="min-h-16 text-sm resize-none"
                                              placeholder="Add comment..."
                                              rows={2}
                                            />
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={3}
                                        className="p-3 text-muted-foreground text-sm text-center italic pl-12"
                                      >
                                        No fields configured for this
                                        subsection.
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="p-3 text-muted-foreground text-sm text-center italic pl-6"
                                >
                                  No subsections configured for this section.
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </SynchronizedPdfViewer>
                ) : (
                  /* Normal Layout - Full Width Form */
                  <div className="overflow-hidden border rounded-lg">
                    <table className="w-full table-fixed">
                      <colgroup>
                        <col className="w-1/4" />
                        <col className="w-5/12" />
                        <col className="w-1/3" />
                      </colgroup>
                      <tbody>
                        {processedData.sections.map((section) => (
                          <React.Fragment key={section.id}>
                            {/* Section Header Row */}
                            <tr>
                              <td
                                colSpan={3}
                                className="p-3 bg-blue-100 font-bold text-left border-b text-blue-900"
                              >
                                {section.title}
                              </td>
                            </tr>

                            {/* Section Subsections */}
                            {section.subsections &&
                            section.subsections.length > 0 ? (
                              section.subsections.map((subsection) => (
                                <React.Fragment key={subsection.id}>
                                  {/* Subsection Header Row */}
                                  <tr>
                                    <td
                                      colSpan={3}
                                      className="p-3 bg-gray-100 font-semibold text-left border-b text-gray-800 pl-6"
                                    >
                                      {subsection.title}
                                    </td>
                                  </tr>

                                  {/* Subsection Fields */}
                                  {subsection.fields &&
                                  subsection.fields.length > 0 ? (
                                    subsection.fields.map((field) => (
                                      <tr
                                        key={field.id}
                                        data-ai-anchor={field.name || undefined}
                                        className={
                                          "border-b transition-colors " +
                                          (activeReview &&
                                          field.name === activeReview.name
                                            ? "bg-violet-100 outline outline-2 -outline-offset-2 outline-violet-400"
                                            : "bg-white")
                                        }
                                      >
                                        <td className="p-3 font-medium text-left border-r align-top pl-12">
                                          {field.title}
                                        </td>
                                        <td className="p-3 text-left border-r align-top">
                                          <div className="space-y-1">
                                            {renderValueEditor(section, subsection, field)}
                                            {renderInputReviewChip(field)}
                                            {renderSuggestionChip(section, subsection, field)}
                                          </div>
                                        </td>
                                        <td className="p-3 text-left align-top">
                                          <div className="space-y-1">
                                            <Textarea
                                              value={field.comment || ""}
                                              onChange={(e) =>
                                                handleFieldCommentChange(
                                                  section.id,
                                                  subsection.id,
                                                  field.id,
                                                  e.target.value
                                                )
                                              }
                                              className="min-h-16 text-sm resize-none"
                                              placeholder="Add comment..."
                                              rows={2}
                                            />
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={3}
                                        className="p-3 text-muted-foreground text-sm text-center italic pl-12"
                                      >
                                        No fields configured for this
                                        subsection.
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="p-3 text-muted-foreground text-sm text-center italic pl-6"
                                >
                                  No subsections configured for this section.
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default OrderDocumentOutput;
